import { datadogLogs } from '@datadog/browser-logs'
import useSWR from 'swr'

import type { MenuRecipeImage } from '@library/menu-service'
import { menuFetcherV3 } from '@library/menu-service'

import { useNumPortions } from 'hooks/basket/useNumPortions'
import { CollectionId } from 'routes/Menu/domains/collections'

const DEFAULT_NUM_PORTIONS = 2

export type RecipeTile = {
  id: string
  name: string
  imageUrl: string
}

const resolveImageUrl = (images: MenuRecipeImage[] = []) => {
  const defaultImageCrops = images?.[0]?.crops || []
  const moodImageCrops = images?.find((image) => image.type === 'mood-image')?.crops
  const crops = moodImageCrops || defaultImageCrops
  const sortedSrcs = crops.slice().sort((a, b) => b.width - a.width)
  const midpointInArray = Math.floor(sortedSrcs.length / 2)

  return sortedSrcs[midpointInArray]?.url || ''
}

export const useRecommendedRecipes = (): { recipes: RecipeTile[]; isLoading: boolean } => {
  const { numPortions } = useNumPortions()
  const { data, error } = useSWR(
    {
      numPortions: numPortions || DEFAULT_NUM_PORTIONS,
      deliveryDate: '',
    },
    menuFetcherV3,
  )

  const isLoading = !data && !error

  if (error) {
    datadogLogs.logger.error(
      `Error fetching menu recipes for upcoming deliveries ${JSON.stringify(error)}`,
    )

    return { recipes: [], isLoading: false }
  }

  if (!data) return { recipes: [], isLoading }

  const recommendedRecipes = data.categories[CollectionId.Recommendations]?.recipes || []
  const allRecipes = data.categories[CollectionId.AllRecipes]?.recipes || []

  const sourceRecipes = recommendedRecipes.length > 0 ? recommendedRecipes : allRecipes

  const topSixRecipes = sourceRecipes.slice(0, 6).map((recipe) => data.recipes[recipe.id])

  const recipes = topSixRecipes.map((recipe) => ({
    id: recipe?.id,
    name: recipe?.name,
    imageUrl: resolveImageUrl(recipe?.images),
  }))

  return { recipes, isLoading }
}
